import StaticPageLayout, { ContentBlock } from 'components/StaticPageLayout'
import Tag from 'components/Tag'
import Svg from 'components/UI/SVG'
import StyledMarkdown from 'components/UI/StyledMarkdown'
import ComponentFAQ from 'components/directus/FAQ'
import DesktopFeatureIcon from 'components/features-page/DesktopFeatureIcon'
import DesktopHowToSetUp from 'components/features-page/DesktopHowToSetUp'
import FeatureBox from 'components/features-page/FeatureBox'
import FeatureImage from 'components/features-page/FeatureImage'
import FeaturePageDownloadButtons from 'components/features-page/FeaturePageDownloadButtons'
import FeaturePageHero from 'components/features-page/FeaturePageHero'
import FeatureSocialProof from 'components/features-page/FeatureSocialProof'
import FeatureVideo from 'components/features-page/FeatureVideo'
import ImageEdgesFeatures from 'components/features-page/ImageEdgesFeature'
import OtherFeatures from 'components/features-page/OtherFeatures'
import WatchAVideoFeature from 'components/features-page/WatchAVideoFeature'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import commandLineIcon from 'images/command-line.svg'
import connectivityIcon from 'images/connectivity.svg'
import windscribeAppMobile from 'images/desktop-app-mobile.png'
import firewallIcon from 'images/firewall.svg'
import ghostIcon from 'images/ghost-icon.svg'
import networkShield from 'images/network-shield.svg'
import protocolsIcon from 'images/protocols.svg'
import proxyIcon from 'images/proxy-icon.svg'
import splitArrowIcon from 'images/split-arrow.svg'
import SplitRouting from 'images/split-routing.svg'
import splitTunnelingIcon from 'images/split-tunneling-icon.svg'
import spoofingIcon from 'images/spoofing.svg'
import toolsIcon from 'images/tools-icon.svg'
import { ReactElement } from 'react'
import rehypeRaw from 'rehype-raw'
import { Flex, Grid, Text, ThemeUIStyleObject } from 'theme-ui'
import useBreakpointIndex from 'theme/useBreakpointIndex'
import getEnglishStrings from 'utils/getEnglishStrings'
import useOSType from 'utils/useOSType'

export const gridBaseStyle: ThemeUIStyleObject = {
  gap: '2.4rem',
  maxWidth: theme => ['100%', '100%', `calc(${theme.sizes?.['maxContentWidth']} + 0.8rem)`],
}

export const imageBaseStyle: ThemeUIStyleObject = {
  objectFit: 'scale-down',
  maxWidth: '50rem',
  width: '100%',
  height: 'auto',
  display: 'flex',
  justifyContent: ['center', 'center', 'normal'],
}

export const otherFeaturesBaseStyle: ThemeUIStyleObject = {
  gap: ['2.4rem', '4rem'],
  maxWidth: ['100%', '100%', 'maxContentWidth'],
}

export default function DesktopFeaturesPage({ path }: { path: string }): JSX.Element {
  const osType = useOSType()
  const data = useStaticQuery<Queries.DesktopFeaturesPageQuery>(graphql`
    query DesktopFeaturesPage {
      directus {
        general_translations {
          strings {
            ikev2
            wireguard
            openvpn
            features
            watch_a_video
            languages_code {
              code
            }
            tcp
            udp
            features
          }
        }
        desktop_page {
          strings {
            languages_code {
              code
            }
            automatic_updating
            automatic_updating_subtitle
            connect_on_any_network
            custom_config
            custom_config_first_subtitle
            custom_config_second_subtitle
            custom_config_support
            custom_config_title
            custom_dns_support
            custom_dns_support_subtitle
            five_different_protocols
            windscribe_offers_linux
            different_protocols
            download_for_macos
            download_for_windows
            features
            firewall
            firewall_first_subtitle
            firewall_second_subtitle
            firewall_title
            ikev2_subtitle
            mac_address_first_subtitle
            mac_address_second_subtitle
            mac_address_spoofing
            mac_address_title
            meme_friendly
            meme_friendly_subtitle
            meta_description
            meta_keywords
            network_and_per_app
            network_level
            network_level_subtitle
            network_rules
            network_rules_first_subtitle
            network_rules_second_subtitle
            network_rules_title
            openvpn_tcp_subtitle
            openvpn_udp_subtitle
            other_features
            packet_size
            packet_size_subtitle
            pre_app_tunneling
            pre_app_tunneling_subtitle
            proxy_gateway
            proxy_gateway_subtitle
            proxy_gateway_title
            secure_hotspot
            secure_hotspot_subtitle
            split_tunneling
            split_tunneling_title
            split_tunneling_subtitle
            stealth
            subtitle
            stealth_subtitle
            tcp_socket_subtitle
            tcp_socket_termination
            title
            tldr_subtitle
            unparalleled_connectivity
            windscribe_offers
            wireguard_subtitle
            wstunnel
            wstunnel_subtitle
            windows_title
            windows_subtitle
            linux_title
            linux_subtitle
            macos_title
            macos_subtitle
            download_for_linux
            windows_video_title
            macos_video_title
            windows_video_url
            mac_video_url
            tldr
          }
          windows_desktop_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          macos_desktop_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          linux_desktop_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          social_proof {
            id
            categories(sort: "sort") {
              social_proof_categories_id {
                id
                strings {
                  languages_code {
                    code
                  }
                  name
                }
                items(sort: "sort") {
                  social_proof_items_id {
                    id
                    url
                    score
                    strings {
                      author_description
                      author_name
                      content
                      languages_code {
                        code
                      }
                    }
                    award_badge {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(height: 30, width: 88)
                        }
                      }
                    }
                    author_picture {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(height: 50, width: 50)
                        }
                      }
                    }
                    organization_picture {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(width: 50, height: 50)
                        }
                      }
                    }
                  }
                }
              }
            }
            strings {
              title
              languages_code {
                code
              }
            }
          }
        }
        linux_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
          }
        }
        windows_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
          }
          id
        }
        macos_app_page {
          strings {
            languages_code {
              code
            }
            id
            meta_description
            meta_keywords
            title
          }
        }
      }
    }
  `)

  const desktopFeaturesData = getEnglishStrings(data.directus?.desktop_page?.strings)
  const generalTranslations = getEnglishStrings(data.directus?.general_translations?.strings)
  const breakpointIndex = useBreakpointIndex()
  const isMobile = breakpointIndex === 0

  const metaData = {
    '/features/windows/': getEnglishStrings(data?.directus?.windows_app_page?.strings),
    '/features/macos/': getEnglishStrings(data?.directus?.macos_app_page?.strings),
    '/features/linux/': getEnglishStrings(data?.directus?.linux_app_page?.strings),
  }[path]

  return (
    <StaticPageLayout
      title={`${metaData?.title} | Windscribe`}
      metaDescription={metaData?.meta_description ?? ''}
      metaKeywords={metaData?.meta_keywords ?? []}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: ['hidden', 'hidden', 'visible'],
          width: '100vw',
          mb: ['4.8rem', '8rem'],
        }}
      >
        <FeaturePageHero path={path} />
        <div
          sx={{
            width: '100%',
            overflow: 'hidden',
            display: ['block', 'none', 'none'],
          }}
        >
          <img
            src={windscribeAppMobile}
            alt=""
            sx={{ width: '100vw', height: 'auto', overflow: 'hidden', mt: '4.8rem' }}
          />
        </div>

        <ContentBlock sxx={{ gap: ['4.8rem', '4.8rem', '8rem'], maxWidth: '98rem' }}>
          <DesktopHowToSetUp sxx={{ mt: '2.4rem' }} />
          <FeatureVideo />
          <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: ['4rem', '4.8rem'] }}>
            <Tag title={generalTranslations?.features ?? ''} />
            <Grid
              columns={[1, 2, 4]}
              sx={{
                gap: '4.8rem',
                justifyContent: 'center',
                alignItems: 'flex-start',
                px: ['2.4rem', '7.2rem', 0],
                maxWidth: '64.7rem',
              }}
            >
              <DesktopFeatureIcon
                icon={protocolsIcon}
                text={
                  path === '/features/linux/'
                    ? desktopFeaturesData?.five_different_protocols ?? ''
                    : desktopFeaturesData?.different_protocols ?? ''
                }
              />
              <DesktopFeatureIcon
                icon={splitArrowIcon}
                text={desktopFeaturesData?.network_and_per_app ?? ''}
              />
              <DesktopFeatureIcon
                icon={spoofingIcon}
                text={desktopFeaturesData?.mac_address_spoofing ?? ''}
              />
              <DesktopFeatureIcon
                icon={commandLineIcon}
                text={desktopFeaturesData?.custom_config ?? ''}
              />
            </Grid>
          </Flex>

          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <FeatureImage
              title={desktopFeaturesData?.connect_on_any_network ?? ''}
              category={desktopFeaturesData?.unparalleled_connectivity ?? ''}
              firstSubtitle={
                path === '/features/linux/'
                  ? desktopFeaturesData?.windscribe_offers_linux ?? ''
                  : desktopFeaturesData?.windscribe_offers ?? ''
              }
              icon={connectivityIcon}
              categoryColour="windscribeBlue"
              imageSide="left"
              sxx={{
                background:
                  theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.windscribeBlue25}, ${theme.colors?.transparent}) border-box`,
                border: '0.2rem solid transparent',
                borderRadius: '4.8rem 4.8rem 0 0',
                p: '2.4rem',
                '& h2:last-child': {
                  width: ['min-content', '100%'],
                },
              }}
              imageStyles={imageBaseStyle}
            >
              <StaticImage src={`../../images/firewall-mode.png`} alt="" />
            </FeatureImage>
            <Grid
              columns={[1, 2, 2]}
              sx={{
                ...gridBaseStyle,
              }}
            >
              <FeatureBox
                title={generalTranslations?.wireguard ?? ''}
                subtitle={desktopFeaturesData?.wireguard_subtitle ?? ''}
                colour="windscribeBlue"
              />
              {path !== '/features/linux/' && (
                <FeatureBox
                  title={generalTranslations?.ikev2 ?? ''}
                  subtitle={desktopFeaturesData?.ikev2_subtitle ?? ''}
                  colour="windscribeBlue"
                />
              )}
              <FeatureBox
                title={`${generalTranslations?.openvpn} (${generalTranslations?.tcp})`}
                subtitle={desktopFeaturesData?.openvpn_tcp_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={`${generalTranslations?.openvpn} (${generalTranslations?.udp})`}
                subtitle={desktopFeaturesData?.openvpn_udp_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={desktopFeaturesData?.stealth ?? ''}
                subtitle={desktopFeaturesData?.stealth_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={desktopFeaturesData?.wstunnel ?? ''}
                subtitle={desktopFeaturesData?.wstunnel_subtitle ?? ''}
                colour="windscribeBlue"
              />
            </Grid>
          </Flex>

          <FeatureImage
            title={desktopFeaturesData?.firewall_title ?? ''}
            category={desktopFeaturesData?.firewall ?? ''}
            firstSubtitle={desktopFeaturesData?.firewall_first_subtitle ?? ''}
            secondSubtitle={desktopFeaturesData?.firewall_second_subtitle ?? ''}
            icon={firewallIcon}
            categoryColour="neonGreen"
            videoButton={{
              href: 'https://www.youtube.com/watch?v=fUlAmYH8c4k',
              text: generalTranslations?.watch_a_video ?? '',
            }}
            imageSide="right"
            sxx={{
              flexDirection: ['column-reverse', 'column', 'row'],
              p: 0,
            }}
            imageStyles={{
              maxWidth: ['100%', '40rem'],
              width: '100%',
              mb: ['4rem', 0],
            }}
          >
            <StaticImage src="../../images/connection-desktop.png" alt="" objectFit="contain" />
          </FeatureImage>
          <Flex
            sx={{
              maxWidth: theme => `calc(${theme.sizes?.['maxContentWidth']} + 0.8rem)`,
              flexDirection: 'column',
            }}
          >
            <Flex sx={{ display: ['flex', 'flex', 'none'], width: '100%' }}>
              <ImageEdgesFeatures
                title={desktopFeaturesData?.split_tunneling_title ?? ''}
                category={desktopFeaturesData?.split_tunneling ?? ''}
                firstSubtitle={desktopFeaturesData?.split_tunneling_subtitle ?? ''}
                icon={splitTunnelingIcon}
                categoryColour={'gold'}
                sxx={{
                  background:
                    theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.yellow25}, ${theme.colors?.transparent}) border-box`,
                  border: '0.2rem solid transparent',
                  borderRadius: '4.8rem 4.8rem 0 0',
                  p: '2.4rem',
                }}
                imageLeft={
                  <StaticImage
                    src="../../images/vertical-split-tunneling-top.png"
                    alt=""
                    sx={{
                      ...imageBaseStyle,
                      minHeight: '24.4rem',
                      maxWidth: '40rem',
                    }}
                    imgStyle={{ objectFit: 'contain' }}
                  />
                }
                imageRight={
                  <StaticImage
                    src="../../images/vertical-split-tunneling-bottom.png"
                    alt=""
                    sx={{
                      ...imageBaseStyle,
                      minHeight: '24.4rem',
                      maxWidth: '40rem',
                    }}
                    imgStyle={{ objectFit: 'contain' }}
                  />
                }
                videoButton={{
                  href: 'https://www.youtube.com/watch?v=WvP7wwNNyRo',
                  text: generalTranslations?.watch_a_video ?? '',
                }}
              />
            </Flex>
            <Flex sx={{ display: ['none', 'none', 'flex'] }}>
              <ImageEdgesFeatures
                title={desktopFeaturesData?.split_tunneling_title ?? ''}
                category={desktopFeaturesData?.split_tunneling ?? ''}
                firstSubtitle={desktopFeaturesData?.split_tunneling_subtitle ?? ''}
                icon={splitTunnelingIcon}
                categoryColour={'gold'}
                sxx={{
                  background:
                    theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.yellow25}, ${theme.colors?.transparent}) border-box`,
                  border: '0.2rem solid transparent',
                  borderRadius: '4.8rem 4.8rem 0 0',
                  p: ['2.4rem', '4.8rem 2.4rem'],
                }}
                imageLeft={
                  <StaticImage
                    src="../../images/horizontal-split-tunneling-left.png"
                    alt=""
                    sx={{ ...imageBaseStyle, mr: '4.8rem', maxHeight: '47rem' }}
                  />
                }
                imageRight={
                  <StaticImage
                    src="../../images/horizontal-split-tunneling-right.png"
                    alt=""
                    sx={{ ...imageBaseStyle, ml: '4.8rem', maxHeight: '47rem' }}
                  />
                }
                {...(osType !== 'linux' && {
                  videoButton: {
                    href: 'https://www.youtube.com/watch?v=WvP7wwNNyRo',
                    text: generalTranslations?.watch_a_video ?? '',
                  },
                })}
              />
            </Flex>
            <Grid
              columns={[1, 2, 2]}
              sx={{
                ...gridBaseStyle,
              }}
            >
              <FeatureBox
                title={desktopFeaturesData?.pre_app_tunneling ?? ''}
                subtitle={desktopFeaturesData?.pre_app_tunneling_subtitle ?? ''}
                colour="yellow"
                icon={SplitRouting}
              />
              <FeatureBox
                title={desktopFeaturesData?.network_level ?? ''}
                subtitle={desktopFeaturesData?.network_level_subtitle ?? ''}
                colour="yellow"
                icon={SplitRouting}
              />
            </Grid>
          </Flex>
          <Flex sx={{ flexDirection: 'column' }}>
            <StaticImage
              src="../../images/mac-spoofing-desktop.png"
              alt=""
              sx={{
                maxWidth: ['0rem', '72rem', 'maxContentWidth'],
                height: '100%',
                display: ['none', 'none', 'block'],
              }}
            />
            <StaticImage
              src="../../images/mac-spoofing-tablet.png"
              alt=""
              sx={{
                maxWidth: ['0rem', '100%', 'maxContentWidth'],
                height: '100%',
                display: ['none', 'block', 'none'],
              }}
            />
            <StaticImage
              src="../../images/mac-spoofing-mobile.png"
              alt=""
              sx={{
                maxWidth: ['unset', '72rem', 'maxContentWidth'],
                height: '100%',
                display: ['block', 'none', 'none'],
                mb: '1.6rem',
              }}
            />
            <Flex
              sx={{
                flexDirection: 'column',
                alignItems: ['center', 'center', 'flex-start'],
              }}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                  alignItems: ['center', 'center', 'flex-start'],
                  textAlign: 'center',
                }}
              >
                <Flex
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '1.6rem',
                    flexDirection: ['row', 'row', 'row'],
                  }}
                >
                  <Svg svg={ghostIcon} />
                  <Text as="h2" color="periwinkle" variant={isMobile ? 'h2Small' : 'h2Big'}>
                    {desktopFeaturesData?.mac_address_spoofing}
                  </Text>
                </Flex>
                <Text
                  as="h3"
                  sx={{
                    mt: '2.4rem',
                    maxWidth: ['100%', '60%', '100%'],
                  }}
                  variant="h2Small"
                >
                  {desktopFeaturesData?.mac_address_title}
                </Text>
              </Flex>
              <Flex
                sx={{
                  mt: '2.4rem',
                  flexDirection: ['column', 'column', 'row'],
                  gap: ['2.4rem', '2.4rem', '2rem'],
                  textAlign: ['center', 'center', 'left'],
                  alignItems: ['center', 'center', 'flex-start'],
                  maxWidth: ['100%', '65%', 'maxContentWidth'],
                  '& > *': {
                    flex: '1 0 0px',
                  },
                }}
              >
                <Text variant="paragraphBigRegular">
                  {desktopFeaturesData?.mac_address_first_subtitle}
                </Text>
                <Text variant="paragraphBigRegular">
                  {desktopFeaturesData?.mac_address_second_subtitle}
                </Text>
              </Flex>
              <Flex sx={{ justifyContent: ['center', 'flex-start'] }}>
                <WatchAVideoFeature
                  colour={'periwinkle'}
                  text={generalTranslations?.watch_a_video ?? ''}
                  href={'https://www.youtube.com/watch?v=WACrWCp1VZQ'}
                  sxx={{ justifyContent: ['center', 'center', 'flex-start'] }}
                />
              </Flex>
            </Flex>
          </Flex>
          <FeatureImage
            category={desktopFeaturesData?.network_rules ?? ''}
            title={desktopFeaturesData?.network_rules_title ?? ''}
            firstSubtitle={desktopFeaturesData?.network_rules_first_subtitle ?? ''}
            secondSubtitle={desktopFeaturesData?.network_rules_second_subtitle ?? ''}
            icon={networkShield}
            categoryColour={'neonGreen'}
            imageSide={'left'}
            videoButton={{
              href: 'https://www.youtube.com/watch?v=H1BvU7QtrNQk',
              text: generalTranslations?.watch_a_video ?? '',
            }}
            sxx={{
              background:
                theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.neonGreen25}, ${theme.colors?.transparent}) border-box`,
              border: '0.2rem solid transparent',
              borderRadius: '4.8rem 4.8rem 0 0',
              p: ['2.4rem', '4.8rem 2.4rem 0 2.4rem'],
            }}
            imageStyles={imageBaseStyle}
          >
            <StaticImage src="../../images/network-whitelist.png" alt="" />
          </FeatureImage>
          <FeatureImage
            title={desktopFeaturesData?.custom_config_title ?? ''}
            category={desktopFeaturesData?.custom_config ?? ''}
            firstSubtitle={desktopFeaturesData?.custom_config_first_subtitle ?? ''}
            secondSubtitle={desktopFeaturesData?.custom_config_second_subtitle ?? ''}
            icon={toolsIcon}
            categoryColour={'windscribeBlue'}
            imageSide="right"
            sxx={{
              '& h2:last-child': {
                textAlign: 'left',
              },
              p: 0,
            }}
            imageStyles={imageBaseStyle}
          >
            <>
              <StaticImage
                src="../../images/custom-config-support-desktop.png"
                alt=""
                sx={{
                  display: ['none', 'none', 'block'],
                }}
              />
              <StaticImage
                src="../../images/custom-config-support-tablet.png"
                alt=""
                sx={{
                  display: ['block', 'block', 'none'],
                }}
              />
            </>
          </FeatureImage>
          <FeatureImage
            title={desktopFeaturesData?.proxy_gateway_title ?? ''}
            category={desktopFeaturesData?.proxy_gateway ?? ''}
            firstSubtitle={desktopFeaturesData?.proxy_gateway_subtitle ?? ''}
            icon={proxyIcon}
            categoryColour={'gold'}
            imageSide="left"
            sxx={{
              background:
                theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.yellow25}, ${theme.colors?.transparent}) border-box`,
              border: '0.2rem solid transparent',
              borderRadius: '4.8rem 4.8rem 0 0',
              p: ['2.4rem', '4.8rem 2.4rem 0 2.4rem'],
            }}
            imageStyles={imageBaseStyle}
          >
            <div sx={{ mb: ['4rem', 0] }}>
              <StaticImage src="../../images/share-app.png" alt="" />
            </div>
          </FeatureImage>
          <OtherFeatures />
          <FeaturePageDownloadButtons hideSignUp path={path} />
          <FeatureSocialProof
            data={
              data.directus?.desktop_page
                ?.social_proof as Queries.DirectusData_component_social_proof
            }
          />
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: theme => `calc(${theme.sizes?.['maxContentWidth']} + 0.8rem)`,
              width: '100%',
            }}
          >
            <ComponentFAQ
              data={
                {
                  windows: data.directus?.desktop_page?.windows_desktop_faq,
                  macos: data.directus?.desktop_page?.macos_desktop_faq,
                  linux: data.directus?.desktop_page?.linux_desktop_faq,
                }[osType] as Queries.DirectusData_component_faq
              }
            />
          </Flex>
          <Text variant="paragraphSmallest" sx={{ maxWidth: '57.2rem', textAlign: 'center' }}>
            {desktopFeaturesData?.tldr ?? ''}
          </Text>
        </ContentBlock>
      </Flex>
    </StaticPageLayout>
  )
}

export function FeatureInstallStep({
  title,
  subtitle,
  order,
}: {
  title: string
  subtitle: string
  order: number
}): ReactElement {
  return (
    <Flex
      sx={{
        p: '2.4rem',
        backgroundColor: 'neonGreen05',
        width: '100%',
        maxWidth: ['100%', '28.4rem'],
        height: '100%',
        borderRadius: '1.6rem',
        border: ({ colors }) => `2px solid ${colors?.neonGreen25}`,
      }}
    >
      <Flex sx={{ gap: '1.6rem', flexDirection: 'column' }}>
        <Flex sx={{ gap: '1.2rem', alignItems: 'center', color: 'neonGreen' }}>
          <div
            sx={{
              variant: 'text.paragraphBigBold',
              p: '0.8rem',
              height: '3.2rem',
              width: '3.2rem',
              backgroundColor: 'neonGreen10',
              borderRadius: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'neonGreen',
            }}
          >
            <Text>{order}</Text>
          </div>
          <Text variant="h3BigBold" sx={{ color: 'neonGreen' }} as="h3">
            {title}
          </Text>
        </Flex>
        <StyledMarkdown
          rehypePlugins={[rehypeRaw]}
          sxx={{
            color: 'white80',
            a: {
              textDecoration: 'underline',
              // necessary otherwise we get the default white80
              color: 'neonGreen',
            },
          }}
          components={{
            p: ({ children }) => <Text variant="paragraphBigRegular">{children}</Text>,
          }}
        >
          {subtitle}
        </StyledMarkdown>
      </Flex>
    </Flex>
  )
}

export function FeatureInstallStepSeparator(): ReactElement {
  return (
    <>
      <div
        sx={{
          width: '6rem',
          display: ['none', 'block'],
          backgroundColor: 'neonGreen25',
          height: '0.2rem',
        }}
      />
      <div
        sx={{
          height: '1.6rem',
          width: '0.2rem',
          display: ['block', 'none'],
          backgroundColor: 'neonGreen25',
        }}
      />
    </>
  )
}
